import React, { useState, useEffect } from 'react';
import { cn } from '../../shared/utils/cn';
import { supabase } from '../../shared/services/supabase';
import { Avatar, AvatarFallback } from '../../shared/components/ui/Avatar/Avatar';

interface CampaignPreview {
  id: string;
  title: string;
  client: string | null;
  icon_url: string | null;
  description: string | null;
  compensation_type: string | null;
}

export const CampaignPreviewSection: React.FC = () => {
  const [campaigns, setCampaigns] = useState<CampaignPreview[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchCampaigns() {
      try {
        // Try to fetch active campaigns (public view)
        // This will work if RLS allows unauthenticated access to v_campaign_frontend
        const { data, error } = await supabase
          .from('v_campaign_frontend')
          .select('id, title, client, icon_url, description, compensation_type, status')
          .eq('status', 'active')
          .limit(3)
          .order('created_at', { ascending: false });

        if (error) {
          console.warn('Could not fetch campaigns for preview:', error);
          setCampaigns([]);
          return;
        }

        const previews: CampaignPreview[] = (data || []).map((campaign: any) => ({
          id: campaign.id,
          title: campaign.title || 'Campaign',
          client: campaign.client || null,
          icon_url: campaign.icon_url || null,
          description: campaign.description || null,
          compensation_type: campaign.compensation_type || null,
        }));

        setCampaigns(previews);
      } catch (e) {
        console.error('Error fetching campaign previews:', e);
        setCampaigns([]);
      } finally {
        setLoading(false);
      }
    }

    fetchCampaigns();
  }, []);

  // Don't render if no campaigns or still loading
  if (loading || campaigns.length === 0) {
    return null;
  }

  const formatLabel = (value: string | null): string => {
    if (!value) return '';
    return value
      .toString()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <div className="w-full py-4 lg:py-6">
      <div className="text-center mb-4">
        <p className="font-satoshi font-medium text-sm text-muted-foreground lg:text-base lg:leading-relaxed mb-2">
          <span className="font-bold">Featured Campaigns</span> — Join creators earning with these opportunities
        </p>
      </div>

      {/* Campaign preview cards */}
      <div className="flex flex-col sm:flex-row gap-3 lg:gap-4 justify-center items-stretch max-w-4xl mx-auto px-4">
        {campaigns.map((campaign) => (
          <div
            key={campaign.id}
            className={cn(
              'flex-1 min-w-0 rounded-xl border border-gray-200 dark:border-gray-700',
              'bg-white dark:bg-gray-900 p-4',
              'hover:shadow-md transition-shadow duration-200'
            )}
          >
            {/* Client icon and name */}
            <div className="flex items-center gap-2 mb-2">
              {campaign.icon_url ? (
                <img
                  src={campaign.icon_url}
                  alt={campaign.client || campaign.title}
                  className="w-6 h-6 rounded-full object-cover shrink-0"
                />
              ) : (
                <Avatar className="w-6 h-6 shrink-0">
                  <AvatarFallback className="text-xs">
                    {(campaign.client || campaign.title || 'C').slice(0, 1).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              )}
              {campaign.client && (
                <span className="text-xs text-muted-foreground truncate font-satoshi">
                  {campaign.client}
                </span>
              )}
            </div>

            {/* Campaign title */}
            <h3 className="font-satoshi font-bold text-base leading-snug text-foreground mb-2 line-clamp-2 text-left">
              {campaign.title}
            </h3>

            {/* Description */}
            {campaign.description && (
              <p className="font-satoshi font-medium text-sm leading-snug text-muted-foreground mb-3 line-clamp-2 text-left">
                {campaign.description}
              </p>
            )}

            {/* Compensation badge */}
            {campaign.compensation_type && (
              <div className="flex items-center">
                <span className="inline-flex items-center px-2 py-1 rounded-md bg-orange-50 dark:bg-orange-950/20 text-orange-700 dark:text-orange-300 text-xs font-medium font-satoshi">
                  {campaign.compensation_type.toLowerCase() === 'paid' ? '💰 ' : campaign.compensation_type.toLowerCase() === 'gifted' ? '🎁 ' : ''}
                  {formatLabel(campaign.compensation_type)}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
